import React from 'react';
import { AppShell, AnimatedPage, type NavSection } from '@theonefamily/ui';
const HubBar = React.lazy(() => import('hubBar/HubBar').then(m => ({ default: m.HubBar })));
import {
  LayoutDashboard,
  Siren,
  Shield,
  Search,
  Play,
  Globe,
  CheckSquare,
  FileText,
  Eye,
  Brain,
  BarChart3,
  Settings,
  Bell,
  Microscope,
  Crosshair,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useEntitlements } from '../hooks/useEntitlements';

const I = (icon: unknown) => icon as NavSection['items'][0]['icon'];

const HUB_BILLING_URL = (import.meta.env.VITE_HUB_URL ?? 'https://hub.theonestack.com') + '/billing';

function upgradeSection(product: string, label: string): NavSection {
  return {
    label,
    items: [
      {
        id: `upgrade-${product}`,
        label: `Upgrade to ${label}`,
        icon: I(Shield),
        href: HUB_BILLING_URL,
        badge: 'Upgrade',
      },
    ],
  };
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout } = useAuth();
  const { entitlements } = useEntitlements();

  const navigation: NavSection[] = [
    // Always-visible section
    {
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: I(LayoutDashboard), href: '/' },
        { id: 'reports', label: 'Reports', icon: I(BarChart3), href: '/reports' },
        { id: 'settings', label: 'Settings', icon: I(Settings), href: '/settings' },
      ],
    },
  ];

  // SOC section
  if (entitlements.soc_enabled) {
    navigation.push({
      label: 'SOC / SIEM',
      items: [
        { id: 'secops', label: 'Command Center', icon: I(Siren), href: '/secops' },
        { id: 'alerts', label: 'Alerts', icon: I(Shield), href: '/secops/alerts' },
        { id: 'incidents', label: 'Incidents', icon: I(Siren), href: '/secops/incidents' },
        { id: 'playbooks', label: 'Playbooks', icon: I(Play), href: '/secops/playbooks' },
        { id: 'threat-hunting', label: 'Threat Hunting', icon: I(Search), href: '/secops/threat-hunting' },
        { id: 'threat-intel', label: 'Threat Intel', icon: I(Globe), href: '/secops/threat-intel' },
        { id: 'response-actions', label: 'Response Actions', icon: I(Shield), href: '/secops/response-actions' },
        { id: 'escalation', label: 'Escalation', icon: I(Bell), href: '/secops/escalation' },
        { id: 'log-sources', label: 'Log Sources', icon: I(FileText), href: '/secops/log-sources' },
        { id: 'investigations', label: 'Investigations', icon: I(Microscope), href: '/secops/forensics' },
        { id: 'investigation-workbench', label: 'Workbench', icon: I(Search), href: '/secops/investigation-workbench' },
        { id: 'ioc-search', label: 'IOC Search', icon: I(Crosshair), href: '/secops/ioc-search' },
        { id: 'detection-rules', label: 'Detection Rules', icon: I(FileText), href: '/secops/detection-rules' },
        { id: 'threat-model', label: 'Threat Model', icon: I(Brain), href: '/secops/threat-model' },
        { id: 'ueba', label: 'UEBA', icon: I(Eye), href: '/secops/ueba' },
        { id: 'hunt-marketplace', label: 'Hunt Marketplace', icon: I(Search), href: '/secops/hunt-marketplace' },
        { id: 'playbook-builder', label: 'Playbook Builder', icon: I(Brain), href: '/secops/playbook-builder' },
        { id: 'autopilot', label: 'AI Autopilot', icon: I(Brain), href: '/secops/autopilot' },
        { id: 'attack-sim', label: 'Attack Sim', icon: I(Siren), href: '/secops/attack-sim' },
        { id: 'deception', label: 'Deception', icon: I(Eye), href: '/secops/deception' },
        { id: 'correlations', label: 'Correlations', icon: I(Globe), href: '/secops/correlations' },
        { id: 'cross-client-threats', label: 'Cross-Client Threats', icon: I(Shield), href: '/secops/cross-client-threats' },
        { id: 'log-analysis', label: 'Log Analysis', icon: I(Search), href: '/secops/log-analysis' },
        { id: 'analytics', label: 'Analytics', icon: I(BarChart3), href: '/secops/analytics' },
      ],
    });
  } else {
    navigation.push(upgradeSection('soc', 'The One SOC'));
  }

  // Compliance — moved to The One Compliance (standalone product)
  navigation.push({
    label: 'Compliance',
    items: [
      {
        id: 'compliance-moved',
        label: 'Open The One Compliance',
        icon: I(CheckSquare),
        href: 'https://app.theonecompliance.app',
      },
    ],
  });

  // Protect features moved to The One Protect (app.theoneprotect.app) — no sidebar section needed

  return (
    <>
      <React.Suspense fallback={<div style={{ height: 44 }} />}>
        <HubBar
          currentProduct="security"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonestack.com'}
          hubUrl={import.meta.env.VITE_HUB_URL}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'security',
            platformName: 'The One Security',
          }}
        />
      </React.Suspense>
      <AppShell
        product={{ name: 'The One Security', accent: '#4f46e5' }}
        navigation={navigation}
        user={{
          name: user?.name ?? user?.email ?? 'User',
          email: user?.email ?? '',
        }}
        onSignOut={logout}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- @types/react version mismatch between local (v19) and monorepo root (v18) */}
        <AnimatedPage>{children as any}</AnimatedPage>
      </AppShell>
    </>
  );
}
